import { Component } from '@theme/component';
import { computeRuptureButtonState } from '@theme/rupture-button-state';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @typedef {Object} AxVariantCardRefs
 * @property {HTMLDetailsElement[]} [selects] - Custom flavor dropdown <details> wrappers.
 * @property {HTMLElement[]} [triggerLabels] - Span showing the selected option name (per dropdown).
 * @property {HTMLElement[]} [triggerSwatches] - Swatch indicator in the trigger (per dropdown).
 * @property {HTMLInputElement} [variantId] - Hidden input with the current variant ID.
 * @property {HTMLFormElement} [form] - The add-to-cart form.
 * @property {HTMLButtonElement} [addButton] - Submit button.
 * @property {HTMLElement} [priceValue] - Price amount element.
 * @property {HTMLElement} [pricePrefix] - "Dès" prefix element (hidden once a variant is picked).
 * @property {HTMLElement} [ctaLabel] - CTA label element.
 * @property {HTMLElement} [ctaPrice] - Price inside the CTA (`show_cta_price`).
 */

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {boolean} [hidden] - Hidden variant (`custom.hide_variant`), only with `show_all_options`.
 * @property {boolean} available
 * @property {string} [soldOutLabel] - rupture label from custom metafields
 * @property {number} price - price in cents
 * @property {string} [image] - variant featured image URL
 * @property {string} [imageAlt] - variant image alt
 * @property {string[]} options
 */

/**
 * Per-card variant (flavor) selector + add-to-cart for ax-variant-multi-slider.
 *
 * On flavor change : matches the variant across all option values, updates the
 * hidden id / price / availability / trigger swatch+label, closes the dropdown.
 * On submit : adds the selected variant to the cart (Section Rendering refresh
 * of the cart drawer), mirroring product-form-component.
 *
 * @extends {Component<AxVariantCardRefs>}
 */
class AxVariantCardComponent extends Component {
  /** @type {Variant[]} */
  #variants = [];

  /**
   * Rupture flags per variant id (`show_rupture_states`). `null` when the card does not
   * carry them : the CTA then keeps the plain available / sold out labels.
   * @type {Record<string, { oos: boolean, def: boolean, temp: boolean }> | null}
   */
  #ruptureFlags = null;

  /** @type {((e: Event) => void) | null} */
  #outsideHandler = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.#variants = [];
    }

    if (this.dataset.variantRupture) {
      try {
        this.#ruptureFlags = JSON.parse(this.dataset.variantRupture);
      } catch {
        this.#ruptureFlags = null;
      }
    }

    this.#updateAvailability();
    this.#bindOutsideClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindOutsideClose();
  }

  /**
   * Flavor radio change.
   * @param {Event} event
   */
  handleOptionChange(event) {
    const target = event?.target;
    if (!(target instanceof HTMLInputElement)) return;

    const optionIndex = parseInt(target.dataset.optionIndex || '', 10);
    if (Number.isNaN(optionIndex)) return;

    this.#updateTrigger(optionIndex, target.value, target.dataset.swatchColor || '', target);
    this.#closeDropdown(optionIndex);

    this.#updateForm(this.#findCurrentVariant());
    this.#updateAvailability();
  }

  /**
   * Add the selected variant to the cart + refresh the cart drawer sections.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, addButton, variantId } = this.refs;
    if (!form || !variantId?.value || addButton?.disabled) return;

    addButton?.setAttribute('aria-busy', 'true');
    if (addButton) addButton.dataset.state = 'loading';

    const formData = new FormData(form);

    const sectionIds = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    }
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, result.message, result.description, result.errors));
        return;
      }

      // Contrat du product-form (même payload que ax-comparateur-card) :
      // `source: 'product-form-component'` est la whitelist d'auto-ouverture du
      // drawer, et `itemCount` = quantité AJOUTÉE, que cart-icon additionne au
      // compteur. Sans lui, la bulle était remise à zéro (ticket 12796152000).
      this.dispatchEvent(
        new CartAddEvent(result, this.id || 'ax-variant-card', {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('ax-variant-card: add to cart failed', error);
    } finally {
      addButton?.removeAttribute('aria-busy');
      if (addButton) delete addButton.dataset.state;
    }
  }

  /**
   * Recompute each value's availability based on the selected values of the
   * OTHER options, then disable the unavailable ones.
   */
  #updateAvailability() {
    const selected = this.#getSelectedValues();

    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (!(radio instanceof HTMLInputElement)) continue;

      const optIdx = parseInt(radio.dataset.optionIndex || '', 10);
      if (Number.isNaN(optIdx)) continue;

      const candidate = selected.slice();
      candidate[optIdx] = radio.value;

      const matching = this.#findVariant(candidate);
      const available = Boolean(matching?.available);

      // Avec les libellés de rupture, une variante épuisée reste sélectionnable : c'est elle
      // qui affiche "Revient vite" ou "Fin de commercialisation". Seule une combinaison
      // sans variante reste bloquée.
      radio.disabled = this.#ruptureFlags ? !matching : !available;
      radio.dataset.available = String(available);

      const item = radio.closest('.ax-variant-card__option');
      if (item instanceof HTMLElement) {
        item.classList.toggle('ax-variant-card__option--unavailable', !available);
      }
    }
  }

  #findCurrentVariant() {
    return this.#findVariant(this.#getSelectedValues());
  }

  /**
   * Variant matching every option value. A hidden variant (`hidden`, only sent with
   * `show_all_options`) counts as a combination that does not exist.
   * @param {string[]} values
   * @returns {Variant | null}
   */
  #findVariant(values) {
    return this.#variants.find((v) => !v.hidden && this.#optionsEqual(v.options, values)) || null;
  }

  /** @returns {string[]} */
  #getSelectedValues() {
    /** @type {string[]} */
    const values = [];

    for (const input of this.querySelectorAll('input[type="radio"]:checked')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const idx = parseInt(input.dataset.optionIndex || '', 10);
      if (!Number.isNaN(idx)) values[idx] = input.value;
    }

    for (const input of this.querySelectorAll('input[type="hidden"][data-option-static="true"]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const idx = parseInt(input.dataset.optionIndex || '', 10);
      if (!Number.isNaN(idx)) values[idx] = input.value;
    }

    return values;
  }

  /**
   * @param {string[]} a
   * @param {string[]} b
   */
  #optionsEqual(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  /**
   * @param {number} optionIndex
   * @param {string} value
   * @param {string} swatchColor
   * @param {HTMLInputElement} [source] - Radio choisi, source des badges à recopier.
   */
  #updateTrigger(optionIndex, value, swatchColor, source) {
    const details = this.#getDropdown(optionIndex);

    const label = details?.querySelector('.ax-variant-card__select-label');
    if (label) label.textContent = value;

    const swatch = details?.querySelector('.ax-variant-card__select-trigger .ax-variant-card__swatch');
    if (swatch instanceof HTMLElement && swatchColor) swatch.style.backgroundColor = swatchColor;

    this.#syncTriggerBadges(label, source);
  }

  /**
   * Dropdown of an option, by option index. The refs arrays follow the DOM order : with
   * a single dropdown (flavor only), index 0 is not necessarily the flavor option.
   * @param {number} optionIndex
   * @returns {HTMLDetailsElement | null}
   */
  #getDropdown(optionIndex) {
    for (const details of this.refs.selects || []) {
      if (details instanceof HTMLDetailsElement && details.dataset.optionIndex === String(optionIndex)) {
        return details;
      }
    }
    return null;
  }

  // Recopie les badges (« Premium », « New ») de l'option choisie dans le trigger :
  // on clone le markup déjà rendu côté serveur plutôt que de le reconstruire, pour
  // rester aligné sur le Liquid (libellés traduits inclus).
  #syncTriggerBadges(label, source) {
    const holder = label?.parentElement;
    if (!holder || !source) return;

    for (const badge of holder.querySelectorAll('.ax-flavor-badge')) badge.remove();

    const option = source.closest('.ax-variant-card__option-label');
    if (!option) return;

    for (const badge of option.querySelectorAll('.ax-flavor-badge')) {
      holder.appendChild(badge.cloneNode(true));
    }
  }

  /**
   * @param {number} optionIndex
   */
  #closeDropdown(optionIndex) {
    const details = this.#getDropdown(optionIndex);
    if (details) details.open = false;
  }

  /**
   * @param {Variant | null} variant
   */
  #updateForm(variant) {
    const { variantId, priceValue, pricePrefix, addButton, ctaLabel, ctaPrice } = this.refs;

    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (ctaLabel) ctaLabel.textContent = this.dataset.labelSoldOut || '';
      return;
    }

    if (variantId) variantId.value = String(variant.id);
    if (priceValue) priceValue.textContent = this.#formatMoney(variant.price);
    if (ctaPrice) ctaPrice.textContent = this.#formatMoney(variant.price);
    if (pricePrefix) pricePrefix.hidden = true;
    this.#updateImage(variant);

    const state = this.#getButtonState(variant);
    if (addButton) addButton.disabled = state.disabled;
    if (ctaLabel) ctaLabel.textContent = state.text;
  }

  /**
   * CTA label + disabled state. With rupture flags, same cascade as the product page
   * (`rupture-button-state.js`) : "Revient vite", "Fin de commercialisation".
   * @param {Variant} variant
   * @returns {{ disabled: boolean, text: string }}
   */
  #getButtonState(variant) {
    const addText = this.dataset.labelAddToCart || '';
    const soldOutText = this.dataset.labelSoldOut || '';

    if (!this.#ruptureFlags) {
      return variant.available
        ? { disabled: false, text: addText }
        : { disabled: true, text: variant.soldOutLabel || soldOutText };
    }

    const flags = this.#ruptureFlags[String(variant.id)] || {};
    return computeRuptureButtonState(
      {
        available: variant.available,
        outOfStock: flags.oos,
        ruptureDefinitive: flags.def,
        ruptureTemporaire: flags.temp,
      },
      {
        addText,
        soldOutText,
        tempText: this.dataset.labelRuptureTemporaire,
        defText: this.dataset.labelRuptureDefinitive,
      }
    );
  }

  /**
   * Swap the card image to the selected variant's image (parité prod).
   * @param {Variant} variant
   */
  #updateImage(variant) {
    if (!variant.image) return;
    const img = this.querySelector('.ax-variant-card__image');
    if (!(img instanceof HTMLImageElement)) return;
    if (img.src === variant.image) return;

    img.removeAttribute('srcset');
    img.removeAttribute('lazy-src');
    img.removeAttribute('lazy-srcset');
    img.src = variant.image;
    if (variant.imageAlt) img.alt = variant.imageAlt;
  }

  /**
   * Format aligned with Shopify's `| money` (no space before €, comma decimal).
   * @param {number} priceCents
   */
  #formatMoney(priceCents) {
    if (Number.isNaN(priceCents)) return '';
    const amount = priceCents / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency,
      }).format(amount);
      return formatted.replace(/\s+/g, '');
    } catch {
      return `${amount.toFixed(2).replace('.', ',')}€`;
    }
  }

  #bindOutsideClose() {
    this.#outsideHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      for (const details of this.refs.selects || []) {
        if (details instanceof HTMLDetailsElement && details.open && !details.contains(target)) {
          details.open = false;
        }
      }
    };
    document.addEventListener('click', this.#outsideHandler);
  }

  #unbindOutsideClose() {
    if (this.#outsideHandler) {
      document.removeEventListener('click', this.#outsideHandler);
      this.#outsideHandler = null;
    }
  }
}

if (!customElements.get('ax-variant-card')) {
  customElements.define('ax-variant-card', AxVariantCardComponent);
}
